import { ApiStateBase } from 'services/api/types';
import useAuth from '../auth/auth';
import { useABTest } from '../contexts/ABTestContext';

interface DevToolsProps {
  deleteAllStatus: ApiStateBase<string>;
  onDeleteAll: () => void;
}

const DevTools: React.FC<DevToolsProps> = ({
  deleteAllStatus,
  onDeleteAll,
}) => {
  const { isViewer, userId, roles } = useAuth();
  const { tests, getTestValue, setTestValue, resetTests } = useABTest();
  const buildId = process.env.REACT_APP_ENV ?? 'Not Set';

  return (
    <div style={styles.containerStyle}>
      <div style={styles.contentWrapper}>
        <div style={styles.leftSection}>
          <p style={styles.infoText}>
            <strong>User_ID:</strong> {userId}
          </p>
          <p style={styles.infoText}>
            <strong>Build:</strong> {buildId}
          </p>
          <p style={styles.infoText}>
            <strong>Roles:</strong>{' '}
            {roles.length > 0 ? roles.join(', ') : 'No roles assigned'}
          </p>
        </div>

        <div style={styles.rightSection}>
          <div style={styles.abTestHeader}>
            <h3 style={styles.abTestTitle}>A/B Tests</h3>
            <button onClick={resetTests} style={styles.resetButton}>
              Reset
            </button>
          </div>
          {tests.length === 0 ? (
            <p style={styles.noTests}>No A/B tests configured</p>
          ) : (
            <div style={styles.testGrid}>
              {tests.map(test => {
                const isChecked = getTestValue(test.key);
                return (
                  <div
                    key={test.key}
                    style={styles.testItem}
                    onClick={() => setTestValue(test.key, !isChecked)}>
                    <div style={styles.testInfo}>
                      <span style={styles.testLabel}>{test.name}</span>
                      <p style={styles.testDescription}>{test.description}</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={e => {
                        e.stopPropagation();
                        setTestValue(test.key, e.target.checked);
                      }}
                      onClick={e => e.stopPropagation()}
                      style={styles.checkbox}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div style={styles.divider} />

      <div style={styles.dangerZone}>
        <h3 style={styles.dangerZoneTitle}>Danger Zone</h3>
        <button
          onClick={onDeleteAll}
          disabled={isViewer || deleteAllStatus.status === 'loading'}
          style={
            isViewer || deleteAllStatus.status === 'loading'
              ? styles.buttonDisabled
              : styles.buttonEnabled
          }>
          {deleteAllStatus.status === 'loading'
            ? 'Deleting...'
            : 'Delete All Products'}
        </button>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  containerStyle: {
    backgroundColor: '#f9fafb',
    borderBottom: '1px solid #e5e7eb',
    paddingLeft: '3rem',
    paddingRight: '3rem',
    paddingTop: '0.75rem',
    paddingBottom: '0.75rem',
    fontSize: '0.75rem',
    color: '#4b5563',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  } as React.CSSProperties,
  contentWrapper: {
    display: 'flex',
    gap: '3rem',
    alignItems: 'flex-start',
  } as React.CSSProperties,
  leftSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
    minWidth: '250px',
  } as React.CSSProperties,
  infoText: {
    margin: 0,
    lineHeight: '1.5',
  } as React.CSSProperties,
  rightSection: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  } as React.CSSProperties,
  abTestHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.25rem',
  } as React.CSSProperties,
  abTestTitle: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: '#111827',
    margin: 0,
  } as React.CSSProperties,
  resetButton: {
    padding: '0.25rem 0.5rem',
    fontSize: '0.75rem',
    borderRadius: '0.25rem',
    backgroundColor: '#6b7280',
    color: 'white',
    border: 'none',
    cursor: 'pointer',
  } as React.CSSProperties,
  noTests: {
    color: '#9ca3af',
    fontStyle: 'italic',
    margin: 0,
  } as React.CSSProperties,
  testGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '0.75rem',
  } as React.CSSProperties,
  testItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.5rem 0.75rem',
    backgroundColor: 'white',
    borderRadius: '0.25rem',
    border: '1px solid #e5e7eb',
    gap: '0.75rem',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  } as React.CSSProperties,
  testInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.125rem',
    flex: 1,
    minWidth: 0,
  } as React.CSSProperties,
  testLabel: {
    fontSize: '0.75rem',
    fontWeight: 500,
    color: '#111827',
    cursor: 'pointer',
    lineHeight: '1.2',
  } as React.CSSProperties,
  testDescription: {
    fontSize: '0.65rem',
    color: '#6b7280',
    margin: 0,
    lineHeight: '1.3',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  } as React.CSSProperties,
  checkbox: {
    width: '18px',
    height: '18px',
    cursor: 'pointer',
    accentColor: '#F15A29',
    flexShrink: 0,
  } as React.CSSProperties,
  divider: {
    height: '1px',
    backgroundColor: '#e5e7eb',
  } as React.CSSProperties,
  dangerZone: {
    display: 'flex',
    flexDirection: 'row',
    gap: '1rem',
    alignItems: 'center',
  } as React.CSSProperties,
  dangerZoneTitle: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: '#dc2626',
    margin: 0,
  } as React.CSSProperties,
  buttonEnabled: {
    padding: '0.5rem 1rem',
    borderRadius: '0.25rem',
    backgroundColor: '#dc2626',
    color: 'white',
    border: 'none',
    cursor: 'pointer',
  } as React.CSSProperties,
  buttonDisabled: {
    padding: '0.5rem 1rem',
    borderRadius: '0.25rem',
    backgroundColor: '#d1d5db',
    color: '#6b7280',
    cursor: 'not-allowed',
    border: 'none',
  } as React.CSSProperties,
};

export default DevTools;
